import React, { useEffect, useState } from "react";
import { api } from "../../services/api";
import ResourcePricingCard from "../../components/ResourcePricingCard";

const FALLBACK_PRICE = {
  productName: "N/A",
  skuName: "N/A",
  meterName: "N/A",
  currency: "USD",
  unitOfMeasure: "N/A",
  pricePerHr: 0,
  pricePerMonth: 0,
  discountPricePerHr: 0,
  discountPricePerMonth: 0,
};

const AzureStorageAccountPricing = ({ formData, organizationName, onPriceChange, onStatusChange }) => {
  const location = formData.location?.value;
  const performance = formData.performance?.value;
  const redundancyField = performance === "Premium" ? "premiumRedundancy" : "standardRedundancy";
  const redundancy = formData[redundancyField]?.value ?? formData.redundancy?.value;
  const accessTier = formData.accessTier?.value;
  const primaryService = formData.primaryService?.value;
  const primaryWorkload = formData.primaryWorkload?.value;
  const requestKey = JSON.stringify([
    location, performance, redundancy, accessTier, primaryService, primaryWorkload,
  ]);
  const [result, setResult] = useState(null);
  const hasLocation = typeof location === "string" && location.trim() !== "";

  useEffect(() => {
    let active = true;
    setResult(null);
    onPriceChange(null);
    onStatusChange?.({ state: hasLocation ? "loading" : "idle" });
    if (!hasLocation) {
      setResult({ requestKey, unavailable: true, price: FALLBACK_PRICE });
      onPriceChange({ ...FALLBACK_PRICE, marketRatePerMonth: 0 });
      onStatusChange?.({ state: "unavailable" });
      return;
    }

    // Debounce field changes and discard responses for superseded selections.
    const timeout = setTimeout(async () => {
      try {
        const params = Object.fromEntries(
          Object.entries({
            ResourceType: "Storage Account",
            Location: location,
            Performance: performance,
            Redundancy: redundancy,
            AccessTier: accessTier,
            PrimaryService: primaryService,
            PrimaryWorkload: primaryWorkload,
          }).filter(([, value]) => typeof value === "string" && value.trim() !== "")
        );
        const response = await api.get("/api/AzureMetaData/AzurePricing", { params });
        if (!active) return;

        const price = response.data;
        const hasNoPricing = price == null || price === "" ||
          (typeof price === "object" && !Array.isArray(price) && (
            Object.keys(price).length === 0 ||
            (["pricePerHr", "pricePerMonth", "discountPricePerHr", "discountPricePerMonth"]
              .every((key) => price[key] === 0) &&
              price.currency == null && price.unitOfMeasure == null)
          ));
        if (hasNoPricing) {
          setResult({ requestKey, unavailable: true, price: FALLBACK_PRICE });
          onPriceChange({ ...FALLBACK_PRICE, marketRatePerMonth: 0 });
          onStatusChange?.({ state: "unavailable" });
          return;
        }
        if (
          !price ||
          !["pricePerHr", "pricePerMonth", "discountPricePerHr", "discountPricePerMonth"]
            .every((key) => typeof price[key] === "number" && Number.isFinite(price[key]) && price[key] >= 0) ||
          typeof price.currency !== "string" ||
          !/^[A-Z]{3}$/.test(price.currency) ||
          typeof price.unitOfMeasure !== "string" ||
          !price.unitOfMeasure.trim()
        ) {
          throw new Error("The Azure pricing API returned an invalid pricing response.");
        }

        setResult({ requestKey, price });
        onPriceChange({ ...price, marketRatePerMonth: price.pricePerMonth });
        onStatusChange?.({ state: "ready" });
      } catch (error) {
        if (!active) return;
        if (error.response?.status === 404) {
          setResult({ requestKey, unavailable: true, price: FALLBACK_PRICE });
          onPriceChange({ ...FALLBACK_PRICE, marketRatePerMonth: 0 });
          onStatusChange?.({ state: "unavailable" });
          return;
        }
        console.error("Error fetching Azure Storage Account pricing:", error);
        const message = error.response?.data?.message || error.message || "Failed to fetch Azure Storage Account pricing.";
        setResult({
          requestKey,
          error: message,
        });
        onStatusChange?.({ state: "error", message });
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [
    location, performance, redundancy, accessTier, primaryService, primaryWorkload,
    hasLocation, requestKey, onPriceChange, onStatusChange,
  ]);

  if (result?.requestKey !== requestKey || !result.price) return null;
  return <ResourcePricingCard price={result.price} organizationName={organizationName} showUnit showHourly={false} />;
};

export default AzureStorageAccountPricing;
